import { FC, Fragment, ReactNode, useEffect } from 'react';

import {
  IRoutingConfigVersion,
  TRoutingConfigVersionStatus,
} from '@entities/routing/config-version';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { Tooltip } from '@mui/material';
import { useModal } from '@shared/components/Modal';
import RoutingVersionStatusBadge from '@shared/components/RoutingVersionStatusBadge';
import { formatRelativeTime } from '@shared/utils/formatRelativeTime';
import { parseUtcDateTime } from '@shared/utils/parseUtcDateTime';

// Drafts first (the version an operator most likely comes back to), the live
// one next, archived history last.
const STATUS_GROUPS: TRoutingConfigVersionStatus[] = ['draft', 'published', 'archived'];

const GROUP_LABELS: Record<TRoutingConfigVersionStatus, string> = {
  draft: 'Draft',
  published: 'Active',
  archived: 'Archived',
};

interface IProps {
  versions: IRoutingConfigVersion[];
  isError: boolean;
  viewingId: number;
  onView: (id: number) => void;
}

// The design's "switch config" picker, read-only variant: rows only change which
// version is on screen. Activation (Publish/Restore) is a separate operation and
// deliberately has no affordance here.
const VersionListModal: FC<IProps> = ({ versions, isError, viewingId, onView }) => {
  const { closeModal } = useModal();

  // The shared ModalProvider's own Esc handler sits on the never-focused overlay
  // div, so it never fires; this pops the top modal — with the discard
  // confirmation stacked on the picker, the first Esc acts as its Cancel.
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') closeModal();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [closeModal]);

  const viewing = versions.find(version => version.id === viewingId);
  const rest = versions.filter(version => version.id !== viewingId);

  const groups = STATUS_GROUPS.map(status => ({
    status,
    items: rest.filter(version => version.status === status),
  })).filter(group => group.items.length);

  const rowClassName = (version: IRoutingConfigVersion) => {
    if (version.id === viewingId) return 'routing-version-list-row routing-version-list-row--viewing';
    if (version.status === 'published') return 'routing-version-list-row routing-version-list-row--active';
    return 'routing-version-list-row';
  };

  const renderRow = (version: IRoutingConfigVersion, action: ReactNode) => {
    // Every row gets a date: publish date for published/archived, creation
    // date for drafts (CSP-645) — the tooltip says which one it is.
    const dateSource = version.published_at ?? version.created_at;
    const dateKind = version.published_at ? 'Published' : 'Created';

    return (
      <tr key={version.id} className={rowClassName(version)}>
      <td className='routing-version-list-info'>
        <div className='routing-version-list-heading'>
          <span className='routing-version-number'>v{version.id}</span>
          <RoutingVersionStatusBadge status={version.status} />
        </div>
        <div className='routing-version-list-name' title={version.name}>
          {version.name}
        </div>
      </td>
      <td className='routing-version-list-comment' title={version.comment ?? undefined}>
        {version.comment || '—'}
      </td>
      <td
        className='routing-version-list-date'
        title={`${dateKind} ${parseUtcDateTime(dateSource).toLocaleString()}`}
      >
        {formatRelativeTime(dateSource)}
      </td>
      <td className='routing-version-list-action'>{action}</td>
    </tr>
    );
  };

  const viewAction = (id: number) => (
    <Tooltip title='View'>
      <span>
        <button
          type='button'
          aria-label='View'
          className='routing-version-view-btn'
          onClick={() => onView(id)}
        >
          <VisibilityIcon />
        </button>
      </span>
    </Tooltip>
  );

  return (
    <div className='routing-version-list-modal'>
      {/* Not a <header>: the app styles bare header elements as the page top bar */}
      <div>
        <h3 className='routing-version-list-title'>Switch config</h3>
        <p className='routing-version-list-subtitle'>
          Switching is view-only — a version becomes active via Publish or Activate. Nothing here
          changes what runs on prod.
        </p>
      </div>

      <div className='routing-version-list-body'>
        {isError ? (
          <p className='routing-version-switcher-unavailable'>Version list is unavailable.</p>
        ) : (
          <table className='routing-version-list-table'>
            <colgroup>
              <col />
              <col />
              <col className='routing-version-list-col-date' />
              <col className='routing-version-list-col-action' />
            </colgroup>
            <tbody>
              {viewing &&
                renderRow(
                  viewing,
                  <span className='routing-version-list-viewing-marker'>Viewing</span>,
                )}
              {groups.map(group => (
                <Fragment key={group.status}>
                  <tr className='routing-version-list-group-row'>
                    <td colSpan={4}>{GROUP_LABELS[group.status]}</td>
                  </tr>
                  {group.items.map(version => renderRow(version, viewAction(version.id)))}
                </Fragment>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default VersionListModal;
